import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { useStyleThemeColors, fieldStyles, useStylePreview } from '../StyleEditor';
import { finalizeStyle, DEFAULT_STYLE } from '../../lib/layer-style';
import { buildPropertyLayers, pickActiveLayer } from '../../lib/properties-layers';
import { initialStyle, saveStyleDefault } from '../../lib/style-defaults';
import StyleTab from './StyleTab';
import LabelsTab from './LabelsTab';
import InfoTab from './InfoTab';

/**
 * Tabbed properties panel (Wave 5.3): Style / Labels / Info for the active layer, in the
 * right dock (registry id `properties`, lib/panel-registry.js). The active layer is
 * `activeLayerId` (MapView's styleTargetLayer: set by the Style buttons, by
 * `openProperties(layerId)` and by clicking a feature on the map), else the first visible
 * layer. Vector layers edit a draft style with a debounced live preview through
 * `onStyle(layerId, style|null)` (MapView's applyLayerStyle); raster and vector-tile layers
 * drive their own hooks (`raster`, `vt`).
 */

const PREVIEW_DELAY_MS = 250;

function typeLabel(layer) {
  if (layer.kind === 'raster') return 'raster';
  if (layer.kind === 'tiles') return `vector tiles${layer.geomType ? ` (${layer.geomType})` : ''}`;
  return layer.geomType || 'vector';
}

function LayerBody({ layer, appliedStyle, onStyle, tab, setTab, raster, vt, showHeatmap, setShowHeatmap, navigate }) {
  const C = useStyleThemeColors();
  const isVector = layer.kind === 'vector';
  const [draft, setDraft] = useState(() => (isVector ? initialStyle(layer.geomType, appliedStyle) : null));
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState('');
  const baseline = useRef(appliedStyle || null);
  const onStyleRef = useRef(onStyle);
  onStyleRef.current = onStyle;

  const style = draft || DEFAULT_STYLE;
  const info = useStylePreview(style, layer.features);
  const classified = style.mode === 'graduated' || style.mode === 'categorized';
  const blocked = isVector && classified && (!style.attr || !info.preview);

  const set = (patch) => { setDraft((s) => ({ ...s, ...patch })); setDirty(true); setNote(''); };

  // Live preview: push the draft to the map shortly after the last change.
  useEffect(() => {
    if (!isVector || !dirty || blocked) return undefined;
    const t = setTimeout(() => onStyleRef.current(layer.id, finalizeStyle(style, info.preview)), PREVIEW_DELAY_MS);
    return () => clearTimeout(t);
  }, [isVector, dirty, blocked, style, info.preview, layer.id]);

  const apply = () => {
    if (blocked || info.classifying) return;
    const finalized = finalizeStyle(style, info.preview);
    onStyle(layer.id, finalized);
    baseline.current = finalized;
    setDirty(false);
    setNote('Applied');
  };
  const revert = () => {
    onStyle(layer.id, baseline.current);
    setDraft(initialStyle(layer.geomType, baseline.current));
    setDirty(false);
    setNote('Reverted');
  };
  const reset = () => {
    onStyle(layer.id, null);
    baseline.current = null;
    setDraft(initialStyle(layer.geomType, null));
    setDirty(false);
    setNote('Reset to the default style');
  };
  const saveDefault = () => {
    const ok = saveStyleDefault(layer.geomType, style);
    setNote(ok ? `Saved as the default for ${layer.geomType} layers` : 'Could not save the default (this layer has no geometry type)');
  };

  const btn = { padding: '6px 8px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg4, color: C.text1, cursor: 'pointer', fontSize: 11 };

  return (
    <Tabs value={tab} onValueChange={setTab} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <TabsList style={{ margin: '8px 12px 0', width: 'auto' }}>
        <TabsTrigger value="style" style={{ flex: 1 }}>Style</TabsTrigger>
        <TabsTrigger value="labels" style={{ flex: 1 }}>Labels</TabsTrigger>
        <TabsTrigger value="info" style={{ flex: 1 }}>Info</TabsTrigger>
      </TabsList>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12 }}>
        <TabsContent value="style" style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <StyleTab layer={layer} style={style} set={set} info={info} raster={raster} vt={vt}
            showHeatmap={showHeatmap} setShowHeatmap={setShowHeatmap} />
        </TabsContent>
        <TabsContent value="labels" style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <LabelsTab layer={layer} style={style} set={set} />
        </TabsContent>
        <TabsContent value="info" style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <InfoTab layer={layer} navigate={navigate} />
        </TabsContent>
      </div>
      {isVector && tab !== 'info' && (
        <div style={{ padding: 10, borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {blocked && <div style={{ fontSize: 10, color: C.text2 }}>Choose an attribute to classify by; the preview starts once classes exist.</div>}
          {note && <div style={{ fontSize: 10, color: C.emerald }} role="status">{note}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={btn} onClick={reset} title="Remove this layer's style override">Reset</button>
            <button style={btn} onClick={revert} disabled={!dirty} title="Back to the last applied style">Revert</button>
            <button onClick={apply} disabled={!dirty || blocked || info.classifying}
              style={{ flex: 1, padding: '6px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: !dirty || blocked ? 0.5 : 1 }}>
              Apply
            </button>
          </div>
          <button style={{ ...btn, fontSize: 10 }} onClick={saveDefault} disabled={!layer.geomType}>
            Save as default for {layer.geomType || 'this'} layers
          </button>
        </div>
      )}
    </Tabs>
  );
}

export default function PropertiesPanel({
  layers = [], usgsData = {}, importedLayers = [], resultLayers = [], rasterLayers = [], vtLayers = [],
  raster, vt, layerStyles = {}, onStyle, activeLayerId, onActiveLayer, showHeatmap, setShowHeatmap, navigate, onClose,
}) {
  const C = useStyleThemeColors();
  const { sel, lbl } = fieldStyles(C);
  const [tab, setTab] = useState('style');
  const list = useMemo(
    () => buildPropertyLayers({ layers, usgsData, importedLayers, resultLayers, rasterLayers, vtLayers }),
    [layers, usgsData, importedLayers, resultLayers, rasterLayers, vtLayers],
  );
  const active = pickActiveLayer(list, activeLayerId);

  return (
    <div data-testid="properties-panel" style={{ background: C.bg2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>Properties</span>
          {onClose && <button onClick={onClose} aria-label="Close properties" style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>}
        </div>
        <label style={lbl} htmlFor="ugis-properties-layer">Layer</label>
        <select id="ugis-properties-layer" value={active?.id || ''} style={sel}
          onChange={(e) => onActiveLayer?.(e.target.value)}>
          {list.length === 0 && <option value="">No layers</option>}
          {list.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {active && (
          <div style={{ marginTop: 6, fontSize: 10, color: C.text2 }} data-testid="properties-summary">
            <strong style={{ color: C.text0, fontSize: 11 }}>{active.name}</strong>
            {' · '}{typeLabel(active)}
            {active.kind !== 'raster' && ` · ${Number(active.featureCount).toLocaleString()} feature${active.featureCount === 1 ? '' : 's'}`}
          </div>
        )}
      </div>
      {active ? (
        <LayerBody key={active.id} layer={active} appliedStyle={layerStyles[active.id]} onStyle={onStyle}
          tab={tab} setTab={setTab} raster={raster} vt={vt} showHeatmap={showHeatmap} setShowHeatmap={setShowHeatmap} navigate={navigate} />
      ) : (
        <div style={{ padding: 16, fontSize: 11, color: C.text3 }}>No layers to show properties for. Import a file or open a dataset first.</div>
      )}
    </div>
  );
}
